/* eslint-disable max-lines */
import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Selectable, Tooltip } from '@hakimo-ui/shared/ui-base';
import { SelectAsync } from '@hakimo-ui/hakimo/ui-elements';
import { toast } from '@hakimo-ui/hakimo/util';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  useCreateSOP,
  useAlarmTypesByName,
  useDefaultSOP,
  useDeleteSOP,
  useSOP,
} from '@hakimo-ui/hakimo/data-access';
import {
  displayAlarmTypeValue,
  createAlarmTypeChangeHandler,
  createFilteredAlarmTypesGetter,
  createSOPPayloadFromDefaults,
  createInlineDeleteHandlers,
  DeleteConfirmation,
} from '../utils';
import { ExistingSOPItem } from '../existing-sop-item/ExistingSOPItem';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  tenantId: string;
  sopAlarmTypes?: Selectable[];
  onSOPCreated?: (alarmType: Selectable) => void;
}

export function AddSOPModal(props: Props) {
  const {
    isOpen,
    onClose,
    locationId,
    tenantId,
    sopAlarmTypes = [],
    onSOPCreated,
  } = props;
  const [selectedAlarmType, setSelectedAlarmType] = useState<
    Selectable | undefined
  >(undefined);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<Selectable | null>(null);
  const getAlarmTypesByName = useAlarmTypesByName();

  const getFilteredAvailableAlarmTypes = useCallback(
    (name: string) => {
      if (!tenantId) {
        return Promise.resolve([]);
      }
      const filteredGetter = createFilteredAlarmTypesGetter(
        (alarmName: string) => getAlarmTypesByName(alarmName, tenantId),
        sopAlarmTypes
      );
      return filteredGetter(name);
    },
    [sopAlarmTypes, getAlarmTypesByName, tenantId]
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedAlarmType(undefined);
      setIsLoadingDefaults(false);
      setDeleteConfirmation(null);
    }
  }, [isOpen]);

  const createMutation = useCreateSOP(
    () => {
      toast('SOP created successfully', { type: 'success' });
      if (selectedAlarmType && onSOPCreated) {
        onSOPCreated(selectedAlarmType);
      }
      onClose();
    },
    (error) => {
      toast(`Error creating SOP: ${error.message}`, { type: 'error' });
    }
  );

  const defaultSOPQuery = useDefaultSOP(locationId, false);
  const handleCreateSOP = async () => {
    if (!selectedAlarmType) {
      toast('Please select an alarm type', { type: 'warning' });
      return;
    }

    try {
      setIsLoadingDefaults(true);
      const createPayload = await createSOPPayloadFromDefaults(
        defaultSOPQuery,
        tenantId,
        locationId,
        selectedAlarmType.id
      );
      setIsLoadingDefaults(false);
      createMutation.mutate(createPayload);
    } catch (error) {
      setIsLoadingDefaults(false);
      toast('SOP already exists for this alarm type', { type: 'error' });
    }
  };

  const onChangeAlarmType = createAlarmTypeChangeHandler(setSelectedAlarmType);

  const sopQuery = useSOP(
    {
      locationId,
      alarmTypeId: deleteConfirmation?.id,
    },
    undefined,
    !!deleteConfirmation
  );
  const deleteMutation = useDeleteSOP(
    sopQuery.data?.id || '',
    () => {
      toast('SOP deleted successfully', { type: 'success' });
      setDeleteConfirmation(null);
    },
    (error) => {
      toast(`Error deleting SOP: ${error.message}`, { type: 'error' });
    }
  );

  const { handleDeleteRequest, handleConfirmDelete, handleCancelDelete } =
    createInlineDeleteHandlers(setDeleteConfirmation, deleteMutation, sopQuery);

  const modalFooter = (
    <>
      <Button
        onClick={handleCreateSOP}
        variant="primary"
        disabled={
          !selectedAlarmType || isLoadingDefaults || createMutation.isLoading
        }
        loading={isLoadingDefaults || createMutation.isLoading}
      >
        {createMutation.isLoading ? 'Creating...' : 'Create SOP'}
      </Button>
      <Button
        onClick={onClose}
        variant="outline"
        disabled={isLoadingDefaults || createMutation.isLoading}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Modify SOP's for Alarm Type"
      footer={modalFooter}
    >
      <div className="min-w-[32rem] space-y-4 p-4 px-8">
        {deleteConfirmation && (
          <DeleteConfirmation
            alarmType={deleteConfirmation}
            isLoading={deleteMutation.isLoading}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        )}
        <div>
          <h3 className="text-onlight-text-1 dark:text-ondark-text-1 mb-2 font-medium">
            Existing SOPs:
          </h3>
          <div className="space-y-2">
            <ExistingSOPItem
              key="default"
              alarmType={{ id: 'default', name: 'Default SOP' }}
              onDelete={handleDeleteRequest}
              index={1}
            />
            {sopAlarmTypes.map((type: Selectable, index: number) => (
              <ExistingSOPItem
                key={type.id}
                alarmType={type}
                onDelete={handleDeleteRequest}
                index={index + 2}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-onlight-text-1 dark:text-ondark-text-1 font-medium">
              Select Alarm Type for New SOP:
            </h3>
            <Tooltip
              text="New SOPs will inherit content from the default SOP"
              colorModifier="info"
              position="top-right"
              size="medium"
            >
              <InformationCircleIcon className="h-5 w-5" />
            </Tooltip>
          </div>
          <SelectAsync
            label=""
            onChangeQuery={getFilteredAvailableAlarmTypes}
            value={selectedAlarmType}
            onChange={onChangeAlarmType}
            displayValue={displayAlarmTypeValue}
            placeholder="Select an alarm type"
          />
        </div>
      </div>
    </Modal>
  );
}
